<template lang="pug">
web-app-layout(:showLogo="true" :noPadding="true")
  .sticker-checkout-page(:class="{ embedded: isEmbedded }")
    .spotlight_card.fw-b(v-if="!error && item.id")
      // Always show sticker/org details at top
      sticker-input-form(
        :show-form="true"
        :userDetails='details',
        :stickerDetails='item',
        :validationDetails='validationDetails.isValid ? validationDetails : { isValid: true, errors: [] }'
      )

      // Show error state when validation fails
      div(v-if="!isFormValid && !validationDetails.isValid")
        .bg-white.px-20.py-4
          .max-w-400.mx-auto.text-center
            .alert.alert-danger.mb-4(v-if="validationDetails.errors && validationDetails.errors.length > 0")
              div(v-for="error in validationDetails.errors" :key="error") {{ error }}

            .mb-4
              p.fs-16.fw-b.text-dark.mb-2 Unable to Collect Sticker
              p.fs-14.text-muted This sticker is currently unavailable. Please try again later or contact support if the issue persists.

            .d-flex.flex-column.align-items-center
              button.btn.btn-primary.mb-3(@click="goBack" style="width: 300px; max-width: 100%;") Go Back
              button.btn.btn-outline-secondary(@click="goHome" style="width: 300px; max-width: 100%;") Go Home

      // Show checkout form when validation passes
      div(v-else)
        div.bg-white
        .px-20.mb-4.pt-4.fees-title(v-if="showContactInfo") Contact
          el-form(
            ref="refContactForm"
            :rules="rules"
            :model="contactInfo"
            status-icon
            label-position="top"
          )
            el-form-item(
              label="First Name"
              :prop="store.getters['users/me'] === null && !isAnonymous ? 'firstName' : ''"
            )
              el-input(v-model="contactInfo.firstName").input-field-payment

            el-form-item(
              label="Last Name"
              :prop="store.getters['users/me'] === null && !isAnonymous ? 'lastName' : ''"
            )
              el-input(v-model="contactInfo.lastName").input-field-payment

            el-form-item(
              :prop="store.getters['users/me'] === null && !isAnonymous ? 'email' : ''"
            )
              template(#label)
                span Email
              el-input(v-model="contactInfo.email" v-lowercase-email).input-field-payment

            el-form-item(label="Mobile phone number" prop="phone")
              el-input(
                v-model="contactInfo.phone"
                type="tel"
                placeholder="(123) 456-7890"
                v-mask="'(###) ###-####'"
              ).input-field-payment

            el-form-item(v-if="store.getters['users/me'] === null")
              el-checkbox(v-model="isAnonymous" label="Anonymous")
                | Anonymous

        .py-4.px-4
          .max-w-300.mx-auto
            .fs-14.mb-4
              .d-flex.mb-2.fees-title
                div Subtotal
                .ms-auto $0.00
              .d-flex.fees-total-title
                div Total
                .ms-auto Free
          .max-w-300.mx-auto
            .mb-4.lh-sm
              i.fs-12.fw-r.opacity-50(v-html="getLegalText()")

            // Marketing consent checkbox
            marketing-consent-checkbox(
              v-if="shouldShowConsentCheckbox"
              ref="consentCheckboxRef"
              :organization-name="item.creatorTitle || 'the organization'"
              :organization-id="item.entity === 'Organization' ? item.entityId : null"
            )

            .mt-6
              el-button(
                type='success'
                v-loading='loading'
                round
                @click="handleCollectFreeSticker"
                :disabled="!isFormValid"
              ).mb-2.donate-button.checkout-action-button
                span {{ getButtonLabel() }}
            .alert.alert-danger(v-if="error")
              | {{ error }}
          .mx-auto.mt-2
            el-button(
              link
              round
              @click.stop='goBack'
            ).text-muted.donate-button Cancel

    // Error or Loading State
    .error-text(v-else-if="error") {{ error }}
    .loading-state(v-else)
      .text-center
        h6.mb-0.py-2 Loading...
        div Please wait while we prepare your sticker.
</template>

<script lang="ts">
import {
  computed, defineComponent, onMounted, reactive, ref,
} from 'vue';
import { useStore } from 'vuex';
import { mask } from 'vue-the-mask';
import WebAppLayout from '../dashboard/WebAppLayout.vue';
import gqlRequest from '../dashboard/gql-request';
import {
  collectFreeSticker, collectFreeStickerForOrg, collectAnonymousFreeSticker,
} from '@/graphs/stickers';
import { useActingEntity } from '@/composables/useActingEntity';
import { makeRequest } from '@/graphs/index';
import { email, phone, requiredRule } from '@/data/rules';
import isUUID from '@/utils/isUUID';
import { isEmbeddedRoute, isEmbeddedContext, addEmbeddedContextToUrl } from '@/utils/embeddedUtils';
import StickerInputForm from '../../payments/StickerInputForm.vue';
import MarketingConsentCheckbox from '@/components/MarketingConsentCheckbox.vue';
import { freeStickerText, sweepstakesText, ticketStickerText } from '@/data/payment-text';
import lowercaseEmail from '@/directives/lowercase-email.js';
import { validateIntentForSticker, validateIntentForStickerPublic } from '@/graphs/payment';
import { hasActiveConsent, giveConsent } from '@/graphs/gql';
import { sanitizeFreeStickerValidation } from '@/utils/stickerValidation';
import {
  buildStickerOpenCollectionCodeLocation,
  buildStickerPathWithAttribution,
} from '@/utils/stickerShareUrl';
import { notifyStickerCollectionProgressChanged } from '@/composables/useStickerCollectionRefresh';
import { stickerRequiresCollectionCodeMessage } from '@/utils/stickerCollectionMessages';

interface Item {
  id: number | null;
  title: string;
  description: string;
  url: string;
  published: any;
  tags: string[];
  cost: number;
  campaignType: null | string;
  causes: string[];
  entity?: string;
  entityId?: string;
  user?: any;
  organization?: any;
  creatorTitle: any;
  sweepstakesSticker?: any;
  requiresCollectionCode?: boolean;
}

export default defineComponent({
  name: 'StickerCheckout',
  components: {
    WebAppLayout,
    StickerInputForm,
    MarketingConsentCheckbox,
  },
  directives: { mask, lowercaseEmail },
  setup() {
    const {
      route,
      router,
      getSticker,
      getStickerBySlug,
    } = gqlRequest();

    const { slugorid } = route.params;
    const store = useStore();
    const { orgId: actingOrgId } = useActingEntity();

    // Extract embed parameters for embedded context
    const embedOrganizationId = ref(null);
    const embedHost = ref(null);
    // Extract socialShopId and glowId from query parameters
    const socialShopId = ref(route.query.socialShopId || null);
    const glowId = ref(route.query.glowId || null);
    // Check if we're in embedded context and extract embed parameters
    if (route.path.includes('/embed/')) {
      // Try to get organization ID from query parameter (preferred) or URL path
      if (route.query.embedOrganizationId) {
        embedOrganizationId.value = route.query.embedOrganizationId;
      } else {
        // Fallback: try to extract from path if it's an organization embed
        const embedOrgMatch = route.path.match(/\/web\/embed\/organization\/([^/?]+)/);
        if (embedOrgMatch) {
          embedOrganizationId.value = embedOrgMatch[1];
        }
      }

      // Extract embed host from embedSource query parameter
      if (route.query.embedSource) {
        embedHost.value = route.query.embedSource;
      }
    }

    const loading = ref(false);
    const error = ref<string | null>(null);
    const refContactForm = ref(null);
    const consentCheckboxRef = ref<any>(null);
    const isAnonymous = ref(false);
    const hasActiveConsentValue = ref(false);
    const isCheckingConsent = ref(true);

    const contactInfo = reactive({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
    });

    const details = reactive({});
    const validationDetails = reactive({ isValid: true, errors: [] });

    const item = reactive<Item>({
      id: null,
      title: '',
      description: '',
      url: '',
      published: null,
      tags: [],
      cost: 0,
      campaignType: null,
      causes: [],
      user: {
        firstname: '',
        lastname: '',
        avatar: null,
      },
      organization: { name: '', avatar: null },
      creatorTitle: null,
      sweepstakesSticker: null,
    });

    const showContactInfo = computed(() => store.getters['users/me'] === null);

    const isFormValid = computed(() => {
      // Block purchase if validation fails
      if (!validationDetails.isValid) {
        return false;
      }

      if (!showContactInfo.value) {
        return true;
      }

      if (isAnonymous.value) {
        return true;
      }

      return contactInfo.email.length > 0;
    });

    // Check if we're in embedded context
    const isEmbedded = computed(() => isEmbeddedRoute(route.path) || isEmbeddedContext());

    const shouldShowConsentCheckbox = computed(() => {
      // Get organization ID based on sticker entity type
      let organizationId: string | null = null;
      if (item.entity === 'Organization') {
        organizationId = item.entityId || null;
      }

      // Don't show if still checking
      if (isCheckingConsent.value) {
        return false;
      }

      // Don't show if user already has active consent
      if (hasActiveConsentValue.value) {
        return false;
      }

      // Don't show if there's no organization (only show for organization stickers)
      if (!organizationId) {
        return false;
      }

      // Show checkbox
      return true;
    });

    function getCreatorName() {
      if (item.organization?.name) {
        return item.organization.name;
      }
      if (item.user?.firstname && item.user?.lastname) {
        return `${item.user.firstname} ${item.user.lastname}`;
      }
      return '';
    }

    function getButtonLabel() {
      const campaignType = item.campaignType?.toLowerCase();

      // Handle Campaign types
      if (campaignType === 'campaign') {
        return 'Add to Sticker Wall';
      }

      // Handle Sweepstakes
      if (campaignType === 'sweepstakes') {
        return 'Enter to Win';
      }

      // Handle Tickets
      if (campaignType === 'ticket') {
        return 'Add to Sticker Wall';
      }

      // Default for free stickers
      return 'Add to Sticker Wall';
    }

    function getLegalText() {
      const campaignType = item.campaignType?.toLowerCase();

      if (campaignType === 'sweepstakes' || item.sweepstakesSticker) {
        return sweepstakesText;
      }

      if (campaignType === 'ticket') {
        return ticketStickerText;
      }

      return freeStickerText;
    }

    async function checkActiveConsent() {
      // Get organization ID based on sticker entity type
      let organizationId: string | null = null;
      if (item.entity === 'Organization') {
        organizationId = item.entityId || null;
      }

      if (!organizationId) {
        // No organization, skip consent check
        isCheckingConsent.value = false;
        return;
      }

      // Check if mobile app provided hasConsent status via URL
      if (route.query.hasConsent !== undefined) {
        // Mobile app already checked consent status and passed it to us
        hasActiveConsentValue.value = route.query.hasConsent === 'true';
        isCheckingConsent.value = false;
        return;
      }

      // Check if user is logged in
      const me = store.getters['users/me'];

      if (!me || !me.token) {
        // Anonymous user without hasConsent param - show checkbox by default
        hasActiveConsentValue.value = false;
        isCheckingConsent.value = false;
        return;
      }

      try {
        const token = me.token;
        const response = await makeRequest(
          'query',
          hasActiveConsent,
          { orgId: organizationId },
          { token },
        );

        if (response?.errors) {
          // On error, assume no consent and show checkbox
          hasActiveConsentValue.value = false;
        } else {
          hasActiveConsentValue.value = response?.data?.hasActiveConsent || false;
        }
      } catch (error) {
        console.error('Error checking consent:', error);
        // On error, assume no consent and show checkbox
        hasActiveConsentValue.value = false;
      } finally {
        isCheckingConsent.value = false;
      }
    }

    async function submitConsentIfChecked() {
      // Check if consent checkbox is shown and checked
      if (!shouldShowConsentCheckbox.value) {
        return;
      }

      if (!consentCheckboxRef.value) {
        return;
      }

      const isConsentChecked = consentCheckboxRef.value.getConsentValue();

      if (!isConsentChecked) {
        return;
      }

      // Get organization ID based on sticker entity type
      let organizationId: string | null = null;
      if (item.entity === 'Organization') {
        organizationId = item.entityId || null;
      }

      if (!organizationId) {
        return;
      }

      // Check if user is logged in
      const me = store.getters['users/me'];
      if (!me || !me.token) {
        // Anonymous user - consent will be handled after profile creation
        return;
      }

      try {
        const { token } = me;

        const response = await makeRequest(
          'mutation',
          giveConsent,
          { orgId: organizationId },
          { token },
        );

        if (response?.errors) {
          console.error('Error submitting consent:', response.errors);
          // Don't block collection if consent fails
        } else {
          hasActiveConsentValue.value = true;
        }
      } catch (error) {
        console.error('Failed to submit consent:', error);
        // Don't block collection if consent fails
      }
    }

    async function validateStickerIntent(stickerId: string) {
      try {
        let validateResponse;

        if (store.getters['users/me'] !== null) {
          // Logged in user
          // Collected by whoever the user is acting as
          const entityType = actingOrgId.value ? 'Organization' : 'User';
          const entityId = actingOrgId.value ?? store.getters['users/me'].id;

          validateResponse = await makeRequest(
            'query',
            validateIntentForSticker,
            { stickerId, entity: entityType, entityId },
            null,
          );
        } else {
          // Anonymous user - use public validation
          validateResponse = await makeRequest(
            'query',
            validateIntentForStickerPublic,
            { stickerId },
            null,
          );
        }

        const rawResult = validateResponse?.data?.validateIntentForSticker
          || validateResponse?.data?.validateIntentForStickerPublic;

        if (rawResult) {
          Object.assign(validationDetails, sanitizeFreeStickerValidation(rawResult));
        }
      } catch (e) {
        console.error('Error validating sticker intent:', e);
        // Set validation to failed if error occurs
        Object.assign(validationDetails, {
          isValid: false,
          errors: ['Failed to validate sticker. Please try again.'],
        });
      }
    }

    async function getItem() {
      try {
        let sticker: any = null;
        if (slugorid) {
          if (isUUID(slugorid)) {
            sticker = await getSticker(slugorid);
            if (!sticker) {
              sticker = await getStickerBySlug(slugorid);
            }
          } else {
            sticker = await getStickerBySlug(slugorid);
          }
        }

        if (!sticker) {
          error.value = 'Sticker not found';
          return;
        }

        // Verify this is actually a free sticker, free sweepstakes, or free ticket
        const hasVarients = sticker.varients?.length;
        const hasSweepstakes = sticker.sweepstakesSticker;
        const hasQuickCosts = sticker.quickCosts?.length;
        const isFreeRegularSticker = sticker.cost === 0 && !hasVarients && !hasSweepstakes && !hasQuickCosts;
        const isFreeSweepstakes = sticker.cost === 0 && hasSweepstakes;

        // For tickets with variants, check if at least one variant is free (not all variants)
        // This allows tickets with mixed free/paid tiers to use the checkout flow for free tiers
        const isFreeTicket = sticker.campaignType?.toLowerCase() === 'ticket'
          && hasVarients
          && sticker.varients.some((variant) => variant.isActive !== false && (variant.cost === 0 || variant.cost === '0'));

        if (!isFreeRegularSticker && !isFreeSweepstakes && !isFreeTicket) {
          error.value = 'This sticker is not free. Please use the regular purchase flow.';
          return;
        }

        Object.assign(item, sticker);

        if (sticker.requiresCollectionCode) {
          const slugOrId = sticker.slug || sticker.id;
          const isEmbedCheckout = route.path.includes('/embed/');
          const codeMessage = stickerRequiresCollectionCodeMessage(sticker.title || '');
          error.value = isEmbedCheckout
            ? `${codeMessage} Returning to the sticker page…`
            : `${codeMessage} Go back to the sticker page to enter your code.`;
          Object.assign(validationDetails, {
            isValid: false,
            errors: [error.value],
          });
          const { path, query } = buildStickerOpenCollectionCodeLocation(
            String(slugOrId),
            route.query as Record<string, unknown>,
            isEmbedCheckout,
          );
          router.replace({ path, query });
          return;
        }

        const causesArray = (sticker.causes !== null && sticker.causes !== '')
          ? sticker.causes.split(' |=| ')
          : [];
        item.causes = causesArray;

        // Set up creator details for display
        if (sticker.user) {
          Object.assign(details, {
            id: sticker.user.id,
            firstname: sticker.user.firstname,
            lastname: sticker.user.lastname,
            userAvatar: sticker.user.avatar,
          });
        }

        if (sticker.organization) {
          Object.assign(details, {
            organizationId: sticker.organization.id,
            organizationName: sticker.organization.name,
            organizationAvatar: sticker.organization.avatar,
          });
        }

        // Validate intent for sticker
        await validateStickerIntent(sticker.id);
      } catch (e) {
        console.error('Error in getItem:', e);
        error.value = 'Failed to load sticker data';
      }
    }

    function getRedirectUrl() {
      return import.meta.env.VITE_WEB_BASE_URL;
    }

    async function handleCollectFreeSticker() {
      try {
        if (item.requiresCollectionCode) {
          error.value = stickerRequiresCollectionCodeMessage(item.title || '');
          return;
        }

        loading.value = true;

        // Submit consent if checkbox is checked
        await submitConsentIfChecked();

        if (store.getters['users/me'] != null && actingOrgId.value) {
          await makeRequest('mutation', collectFreeStickerForOrg, {
            orgId: actingOrgId.value,
            stickerId: String(item.id),
            socialShopId: socialShopId.value,
            glowId: glowId.value,
          }, null);
        } else if (store.getters['users/me'] != null) {
          await makeRequest('mutation', collectFreeSticker, {
            stickerId: String(item.id),
            socialShopId: socialShopId.value,
            glowId: glowId.value,
          }, null);
        } else {
          await makeRequest('mutation', collectAnonymousFreeSticker, {
            stickerId: String(item.id),
            email: isAnonymous.value ? '' : contactInfo.email,
            phone: contactInfo.phone.replace(/[^\d]/g, ''),
            firstName: contactInfo.firstName,
            lastName: contactInfo.lastName,
            embedOrganizationId: embedOrganizationId.value,
            embedHost: embedHost.value,
            socialShopId: socialShopId.value,
            glowId: glowId.value,
          }, null);
        }

        if (store.getters['sticker/HAS_FETCHED_PURCHASED_STICKERS']) {
          const currentStickers = [...store.getters['sticker/GET_PURCHASED_STICKERS']];
          if (!currentStickers.some((s) => s.id === item.id)) {
            currentStickers.unshift(item);
            store.dispatch('sticker/SET_PURCHASED_STICKERS', currentStickers);
          }
        }

        notifyStickerCollectionProgressChanged();

        if (store.getters['users/me'] != null) {
          // If glowId is present, redirect back to glow page, otherwise to purchased stickers
          if (glowId.value) {
            router.push(`/web/glow/${glowId.value}`);
          } else {
            router.push('/web/stickers/purchased');
          }
        } else {
          localStorage.setItem('unregisterEmail', contactInfo.email);
          localStorage.setItem('unregisterPhone', contactInfo.phone);
          
          // Check if user checked the consent checkbox and store for mobile app
          let giveConsent = 'false';
          let consentOrgId = '';
          if (shouldShowConsentCheckbox.value && consentCheckboxRef.value) {
            const isConsentChecked = consentCheckboxRef.value.getConsentValue();
            giveConsent = isConsentChecked ? 'true' : 'false';
            
            // Get organization ID for consent
            if (item.entity === 'Organization') {
              consentOrgId = item.entityId || '';
            }
            
            // Store consent info in localStorage for mobile app to read
            if (giveConsent === 'true' && consentOrgId) {
              localStorage.setItem('pendingConsent', 'true');
              localStorage.setItem('pendingConsentOrgId', consentOrgId);
            }
          }
          
          const emailParam = isAnonymous.value ? '' : `?email=${encodeURIComponent(contactInfo.email)}`;
          let completeProfileUrl = `${getRedirectUrl()}/auth/web/complete-profile${emailParam}`;
          
          // Add consent parameters to URL for mobile app to intercept
          if (giveConsent === 'true' && consentOrgId) {
            const separator = emailParam ? '&' : '?';
            completeProfileUrl += `${separator}giveConsent=${giveConsent}&orgId=${consentOrgId}`;
          }
          
          // Preserve embedded context when redirecting
          window.location.href = addEmbeddedContextToUrl(completeProfileUrl);
        }
      } catch (err) {
        console.error('Error collecting free sticker:', err);
        error.value = 'Failed to collect sticker. Please try again.';
      } finally {
        loading.value = false;
      }
    }

    function goBack() {
      // Check if we're in embed mode and handle accordingly
      if (embedOrganizationId.value) {
        // Build URL back to original embed organization page
        let homeUrl = `/web/embed/organization/${embedOrganizationId.value}`;
        if (embedHost.value) {
          homeUrl += `?embedSource=${encodeURIComponent(embedHost.value)}`;
        }
        window.location.href = homeUrl;
        return;
      }

      // Default behavior for non-embed mode
      router.push(buildStickerPathWithAttribution(String(slugorid), {
        socialShopId: socialShopId.value as string | null,
        glowId: glowId.value as string | null,
      }));
    }

    function goHome() {
      // Check if we're in embed mode and handle accordingly
      if (embedOrganizationId.value) {
        let homeUrl = `/web/embed/organization/${embedOrganizationId.value}`;
        if (embedHost.value) {
          homeUrl += `?embedSource=${encodeURIComponent(embedHost.value)}`;
        }
        window.location.href = homeUrl;
        return;
      }

      // Navigate to home page
      router.push('/web');
    }

    onMounted(async () => {
      loading.value = true;
      await getItem();
      loading.value = false;

      // Check for active consent after sticker details are loaded
      await checkActiveConsent();
    });

    return {
      loading,
      error,
      item,
      details,
      validationDetails,
      contactInfo,
      isAnonymous,
      refContactForm,
      consentCheckboxRef,
      showContactInfo,
      isFormValid,
      getCreatorName,
      getButtonLabel,
      handleCollectFreeSticker,
      goBack,
      goHome,
      store,
      rules: {
        firstName: requiredRule('First Name'),
        lastName: requiredRule('Last Name'),
        email,
        phone,
      },
      isEmbedded,
      freeStickerText,
      sweepstakesText,
      ticketStickerText,
      getLegalText,
      hasActiveConsentValue,
      isCheckingConsent,
      shouldShowConsentCheckbox,
    };
  },
});
</script>

<style scoped>
.sticker-checkout-page {
  max-width: 400px;
  margin: 0 auto;
  padding: 24px 24px 40px;
  box-sizing: border-box;

  @media (min-width: 768px) {
    padding: 32px 40px 40px;
  }

  @media (min-width: 1024px) {
    padding: 40px 64px 40px;
  }
}

/* Ensure all child elements respect container bounds */
.sticker-checkout-page * {
  max-width: 100%;
  box-sizing: border-box;
}

.el-form-item {
  margin-bottom: 12px !important;
}

.action-button{
  margin-bottom: 0px !important;
}

.fees-section {
  padding-top: 8px !important;
  padding-bottom: 8px !important;
}

.checkout-action-button {
  background-color: #225c6b !important;
  border-color: #225c6b !important;
  width: 100% !important;
  max-width: 100% !important;
}

/* Override donate-button width in this context to prevent overflow */
.sticker-checkout-page .donate-button {
  width: 100% !important;
  max-width: 100% !important;
  margin: 0 auto !important;
}

/* Reduce excessive horizontal padding on smaller containers */
.sticker-checkout-page .px-20 {
  padding-left: 15px !important;
  padding-right: 15px !important;
}

/* Ensure max-width containers have proper spacing */
.sticker-checkout-page .max-w-300 {
  max-width: 100% !important;
  width: 100% !important;
}

/* Constrain images and other media elements */
.sticker-checkout-page img {
  max-width: 100% !important;
  height: auto !important;
}

/* Ensure form elements don't exceed container bounds */
.sticker-checkout-page .el-input,
.sticker-checkout-page .el-button {
  max-width: 100% !important;
}

.required-star {
  color: #F56C6C;
}

.error-text {
  color: #000000;
  font-size: 16px;
  text-align: center;
  padding: 20px;
  font-weight: 500;
}

.loading-state {
  text-align: center;
  padding: 40px 20px;
}

/* Error state button styling */
.sticker-checkout-page .btn {
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 8px;
  transition: all 0.2s ease;
  border: 2px solid transparent;
}

.sticker-checkout-page .btn-primary {
  background-color: #0d9488;
  color: white;
  border-color: #0d9488;
}

.sticker-checkout-page .btn-primary:hover {
  background-color: #0f766e;
  border-color: #0f766e;
}

.sticker-checkout-page .btn-outline-secondary {
  background-color: white;
  color: #6c757d;
  border-color: #dee2e6;
}

.sticker-checkout-page .btn-outline-secondary:hover {
  background-color: #f8f9fa;
  color: #495057;
  border-color: #adb5bd;
}

/* Embedded view padding */
.sticker-checkout-page.embedded {
  padding: 40px 30px;
  max-width: 450px; /* Slightly wider for embedded context */

  @media (max-width: 768px) {
    padding: 30px 20px;
  }

  /* Adjust button sizes for embedded context */
  .donate-button {
    max-width: 100% !important;
  }

  .max-w-300 {
    max-width: 100% !important;
  }

  .px-20 {
    padding-left: 20px !important;
    padding-right: 20px !important;
  }
}
</style>
